import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, DateRangePickerComponent, DateRange } from '../../../shared/components/primeng';
import { RequestCreditsComponent } from '../../../shared/components/request-credits/request-credits.component';
import { AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';

export interface CreditRequestData {
  id: string;
  userName: string;
  requestedOn: string;
  balance: string;
  expireOn: string;
  status: string;
  action?: string;
  originalData?: any;
  additionalInfo?: any;
}

@Component({
  selector: 'app-user-credit-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
    DateRangePickerComponent,
    RequestCreditsComponent,
  ],
  templateUrl: './user-credit-requests.component.html',
  styleUrl: './user-credit-requests.component.scss',
})
export class UserCreditRequestsComponent implements OnInit, AfterViewInit {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;

  columns: TableColumn[] = [
    { 
      key: 'userName', 
      label: 'Username',
      sortable: true,
      width: '20%'
    },
    { 
      key: 'requestedOn', 
      label: 'Requested On',
      sortable: true,
      width: '18%'
    },
    { 
      key: 'balance', 
      label: 'Balance',
      sortable: false,
      width: '12%'
    },
    { 
      key: 'expireOn', 
      label: 'Expire On',
      sortable: false,
      width: '18%'
    },
    { 
      key: 'status', 
      label: 'Status',
      sortable: false,
      width: '12%'
    },
    { 
      key: 'action', 
      label: 'Action',
      sortable: false,
      width: '20%',
      fixed: 'right'
    },
  ];

  creditRequestsData: CreditRequestData[] = [];
  loading = false;
  emptyMessage = 'No credit requests found';
  openDropdownId: string | null = null;

  /** Current sort state for server-side sorting */
  sortColumn: string | null = 'requestedOn';
  sortDirection: 'asc' | 'desc' = 'desc';
  
  // Approve modal state
  isApproveModalVisible = false;
  selectedRequest: CreditRequestData | null = null;
  newCredits: number | null = 0;
  expirationDate: DateRange | null = null;
  minDate: Date = new Date(); // Today's date as minimum
  
  // View modal state
  isViewModalVisible = false;
  viewRequest: CreditRequestData | null = null;

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  constructor(
    private authApiService: AuthApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Don't load on init - will be called when tab is activated
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.actionCellTemplate) {
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }
    }
    
    if (this.statusCellTemplate) {
      const statusColumn = this.columns.find(col => col.key === 'status');
      if (statusColumn) {
        statusColumn.cellTemplate = this.statusCellTemplate;
      }
    }
  }

  async loadCreditRequests(): Promise<void> {
    this.loading = true;
    try {
      // Call API to get credit requests
      const sortString = this.getSortString();
      const response = await this.authApiService.getCreditRequest(
        this.pagination.currentPage,
        this.pagination.pageSize,
        sortString
      );
      
      // Map response to CreditRequestData format
      let mappedData: CreditRequestData[] = [];
      
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          // Format requested date
          let requestedOn = 'N/A';
          const requestedAt = item.requestedAt || item.requested_at || '';
          if (requestedAt) {
            requestedOn = this.formatService.formatDate(requestedAt);
          }
          
          // Format expiration date
          let expireOn = 'N/A';
          const expirationDate = item.expirationDate || item.expiration_date || item.expireOn || item.expire_on || '';
          if (expirationDate) {
            expireOn = this.formatService.formatDate(expirationDate);
          }
          
          // Format status to PascalCase
          const status = this.formatService.toPascalCase(item.status || 'N/A');
          
          // Format balance
          const balance = item.balance || '0.0';
          
          return {
            id: item.id || '',
            userName: item.userName || item.user_name || 'N/A',
            requestedOn: requestedOn,
            balance: balance,
            expireOn: expireOn,
            status: status,
            action: 'Action',
            originalData: item, // Store original API response data
            additionalInfo: item.additionalInfo || item.additional_info || item.additional || null,
          };
        });
        
        // Update pagination from paginationInfo
        if (response['paginationInfo']) {
          const paginationInfo = response['paginationInfo'];
          this.pagination = {
            ...this.pagination,
            totalItems: paginationInfo['totalCount'] !== undefined ? paginationInfo['totalCount'] : this.pagination.totalItems,
            // Preserve currentPage from the request, or use paginationInfo.page if available
            currentPage: paginationInfo['page'] !== undefined ? paginationInfo['page'] : this.pagination.currentPage,
            pageSize: paginationInfo['size'] !== undefined ? paginationInfo['size'] : this.pagination.pageSize
          };
        }
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        const paginationInfo = response['paginationInfo'];
        if (paginationInfo['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: paginationInfo['totalCount'],
            currentPage: paginationInfo['page'] !== undefined ? paginationInfo['page'] : this.pagination.currentPage,
            pageSize: paginationInfo['size'] !== undefined ? paginationInfo['size'] : this.pagination.pageSize
          };
        }
        mappedData = []; // No data to map
      }
      
      this.creditRequestsData = mappedData;
      
      // Debug log to verify data is loaded
      console.log('Credit requests loaded:', {
        page: this.pagination.currentPage,
        totalItems: this.pagination.totalItems,
        dataCount: mappedData.length,
        data: mappedData
      });
    } catch (error) {
      console.error('Error loading credit requests:', error);
      this.creditRequestsData = [];
    } finally {
      this.loading = false;
    }
  }

  onPageChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: event
    };
    this.loadCreditRequests();
  }

  onPageSizeChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: event,
      currentPage: 1
    };
    this.loadCreditRequests();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadCreditRequests();
  }

  getSortString(): string {
    const dir = this.sortColumn ? this.sortDirection : 'desc';
    const apiFieldMap: Record<string, string> = {
      userName: 'userName',
      requestedOn: 'requestedAt',
      balance: 'balance',
      expireOn: 'expirationDate',
      status: 'status',
    };
    const apiField = (this.sortColumn && apiFieldMap[this.sortColumn]) || 'requestedAt';
    return `${apiField}:${dir}`;
  }

  toggleActionDropdown(event: Event, row: CreditRequestData): void {
    event.stopPropagation();
    const rowId = row.id || '';
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
      this.dropdownPosition = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openDropdownId = rowId;
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  isDropdownOpen(row: CreditRequestData): boolean {
    const rowId = row.id || '';
    return this.openDropdownId === rowId;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): CreditRequestData | null {
    if (this.openDropdownId == null) return null;
    return this.creditRequestsData.find((r) => (r?.id ?? '') === this.openDropdownId) ?? null;
  }

  /** Position for the fixed dropdown overlay. */
  dropdownPosition: { top: number; left: number; width: number } | null = null;

  isRequestProcessed(row: CreditRequestData): boolean {
    // Check if request status is "Granted", "Approved", or "Rejected" (case-insensitive)
    const status = (row.status || '').toLowerCase();
    return status === 'granted' || status === 'approved' || status === 'rejected';
  }

  handleAction(action: string, row: CreditRequestData): void {
    this.closeActionDropdown();
    
    // Don't proceed if request is already processed
    if (this.isRequestProcessed(row) && (action === 'approve' || action === 'reject')) {
      return;
    }
    
    if (action === 'view') {
      // Open view modal
      this.viewRequest = row;
      this.isViewModalVisible = true;
    } else if (action === 'approve') {
      // Open approve modal
      this.selectedRequest = row;
      this.newCredits = 0;
      this.expirationDate = null;
      this.isApproveModalVisible = true;
    } else if (action === 'reject') {
      // Call reject API directly
      this.rejectCreditRequest(row);
    }
  }

  async rejectCreditRequest(row: CreditRequestData): Promise<void> {
    if (!row.id) {
      console.error('Request ID is missing');
      return;
    }

    try {
      // Prepare payload with id and status for reject
      const payload = {
        id: row.id,
        status: 'rejected'
      };

      // Call API to reject credit request
      await this.authApiService.approveCreditRequest(row.id, 'rejected', payload);
      this.toasterService.success('Credit request has been rejected successfully');
      // Reload the data to reflect the updated status
      await this.loadCreditRequests();
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error rejecting credit request:', error);
      // Check if error has detail message
      if (error?.error?.detail) {
        this.toasterService.error(error.error.detail);
      } else {
        this.toasterService.error('Failed to reject credit request. Please try again.');
      }
    }
  }

  getOldCredits(): number {
    if (!this.selectedRequest) return 0;
    const balance = parseFloat(this.selectedRequest.balance || '0');
    return isNaN(balance) ? 0 : balance;
  }

  getTotalCredits(): number {
    const oldCredits = this.getOldCredits();
    const newCreditsValue = this.newCredits || 0;
    return oldCredits + newCreditsValue;
  }

  async onApproveConfirm(): Promise<void> {
    if (!this.selectedRequest || !this.newCredits || this.newCredits <= 0 || !this.expirationDate?.startDate) {
      return;
    }

    if (!this.selectedRequest.id) {
      console.error('Request ID is missing');
      return;
    }

    try {
      // Format amount as a number (not string) with at least 1 decimal place
      // Ensure it's always a float (not an integer) so JSON.stringify preserves the decimal
      const amountValue = this.newCredits!;
      // Use toFixed(1) to ensure at least 1 decimal place, then parseFloat to convert back to number
      // This ensures 432 becomes 432.0 (as a number, not string)
      // Force it to be a float by dividing by 1.0 to ensure it's not an integer
      const formattedAmount = parseFloat(amountValue.toFixed(1)) / 1.0;
      
      // Format expiration date as YYYY-MM-DDTHH:mm:ss (no milliseconds, no timezone)
      const date = this.expirationDate.startDate;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const expirationDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      
      // Prepare payload with required fields for credit approval
      // Note: JSON.stringify will serialize 432.0 as 432, but the value is still a float
      const payload = {
        id: this.selectedRequest.id,
        status: 'granted',
        amount: formattedAmount, // Number type (float with one decimal)
        expiration_date: expirationDate,
      };

      // Call API to approve credit request with status "granted"
      await this.authApiService.approveCreditRequest(this.selectedRequest.id, 'granted', payload);
      
      this.toasterService.success('Credit request has been approved successfully');
      
      // Close modal and reset
      this.closeApproveModal();
      
      // Reload the data to reflect the updated status
      await this.loadCreditRequests();
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error approving credit request:', error);
      // Check if error has detail message
      if (error?.error?.detail) {
        this.toasterService.error(error.error.detail);
      } else {
        this.toasterService.error('Failed to approve credit request. Please try again.');
      }
    }
  }

  closeApproveModal(): void {
    this.isApproveModalVisible = false;
    this.selectedRequest = null;
    this.newCredits = 0;
    this.expirationDate = null;
  }

  closeViewModal(): void {
    this.isViewModalVisible = false;
    this.viewRequest = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending' || statusLower === 'pending approval') {
      return 'status-chip status-chip-pending';
    } else if (statusLower === 'granted' || statusLower === 'approved') {
      return 'status-chip status-chip-granted';
    } else if (statusLower === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    
    return 'status-chip status-chip-default';
  }
}
